<script lang="ts">
  import { onMount } from 'svelte'

  type ToggleButtonGroupVariants = 'default' | 'flat' | 'outlined'

  export let offset = 4
  export let block: boolean | undefined = undefined
  export let active: HTMLButtonElement | undefined = undefined
  export let variant: ToggleButtonGroupVariants | undefined = undefined

  let toggleButtonGroup: HTMLDivElement | undefined = undefined
  let width = 0
  let resizeObserver: ResizeObserver

  $: if (toggleButtonGroup) {
    setActive(active)
  }

  function handleClick(event: MouseEvent) {
    const path = event.composedPath()
    active = path.find((element) => element instanceof HTMLButtonElement)
  }

  function resetStyle(active: HTMLButtonElement | undefined) {
    const buttons = toggleButtonGroup?.querySelectorAll('button')

    buttons?.forEach((button) => {
      if (active !== button) {
        if (variant === 'outlined') {
          button.style.removeProperty('--button-text-color')
        } else {
          button.style.removeProperty('--button-font-weight')
        }
      }
    })
  }

  function setActive(button: HTMLButtonElement | undefined) {
    const active =
      button ?? toggleButtonGroup?.querySelector('button') ?? undefined
    resetStyle(active)

    if (active) {
      width = active.clientWidth
      offset = active.offsetLeft

      if (variant === 'outlined') {
        active.style.setProperty('--button-text-color', '#fff')
      } else {
        active.style.setProperty('--button-font-weight', 'bold')
      }
    }
  }

  onMount(() => {
    resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]

      if (entry) {
        setActive(active)
      }
    })

    if (toggleButtonGroup) {
      resizeObserver.observe(toggleButtonGroup)
    }

    return () => {
      resizeObserver.disconnect()
    }
  })
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class="toggle-button-group{variant ? ` ${variant}` : ''}"
  on:click={handleClick}
  role="button"
  tabindex="0"
  class:block
  bind:this={toggleButtonGroup}
>
  <div
    class="selection"
    style={`transform: translateX(${offset}px); width: ${width}px`}
  ></div>
  <slot />
</div>

<style lang="scss">
  .toggle-button-group {
    position: relative;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    background-color: var(
      --toggle-button-group-background-color,
      var(--color-tertiary)
    );
    border-radius: 2em;
    padding: 0.25em;
    height: max-content;
    max-width: 100%;
    width: max-content;
    --button-text-hover-opacity: 0;
    --button-font-weight: normal;
    z-index: 2;

    &.block {
      width: 100%;
      justify-content: space-evenly;
    }

    .selection {
      position: absolute;
      left: 0;
      height: calc(100% - 0.5em);
      background-color: var(--color-background);
      border-radius: 2em;
      z-index: 0;
      transition:
        transform 300ms cubic-bezier(0.25, 0.8, 0.5, 1),
        width 300ms cubic-bezier(0.25, 0.8, 0.5, 1);
    }

    &.outlined {
      background-color: transparent;
      border: 1px solid #000;
      --button-font-weight: bold;
      padding: 0.5em;

      .selection {
        background-color: #000;
        height: calc(100% - 1em);
      }
    }

    :global(> *) {
      flex: 1;
      z-index: 1;
    }
  }
</style>
